import {Icon} from '@iconify/react';
import React from 'react';
import RankCell from './table/rankCell';
import {formatCurrency} from '../../util/utils';
import {SearchRankingSort} from '../../util/enums';
import {SearchRankingsColumnDef} from '../../util/interfaces';
import i18n from '../../i18n';

function formatVolume(vol: number): string {
    if (vol > 1000) return `${(vol / 1000).toFixed(1).replace('.', ',')}k`;
    return String(vol);
}

function buildAllColumns(): Array<SearchRankingsColumnDef> {
    return [
        //  Performance (default)
        {
            field: 'search_volume',
            sortField: SearchRankingSort.SEARCH_VOLUME,
            label: i18n.t('searchRankings.columns.searchVolume', 'Zoekvolume'),
            className: 'col-metric',
            centered: true,
            group: 'Performance',
            body: product => (
                <div className='volume-cell'>
                    <div className='volume-value'>{formatVolume(product.search_volume)}</div>
                    <div className='volume-label'>{i18n.t('searchRankings.columns.monthly', 'Maandelijks')}</div>
                </div>
            )
        },
        {
            field: 'keyword_count',
            sortField: SearchRankingSort.KEYWORDS,
            label: i18n.t('searchRankings.columns.keywords', 'Keywords'),
            className: 'col-metric-sm',
            centered: true,
            group: 'Performance',
            body: product => (
                <div className='count-cell'>
                    <div className='count-icon'>
                        <Icon icon='mdi:pound' style={{fontSize: '0.75rem'}} />
                    </div>
                    <span className='count-value'>{product.keyword_count}</span>
                </div>
            )
        },
        {
            field: 'category_count',
            sortField: SearchRankingSort.CATEGORIES,
            label: i18n.t('searchRankings.columns.categories', 'Categorie\u00EBn'),
            className: 'col-metric-sm',
            centered: true,
            group: 'Performance',
            body: product => (
                <div className='count-cell'>
                    <div className='count-icon'>
                        <Icon icon='mdi:layers-outline' style={{fontSize: '0.75rem'}} />
                    </div>
                    <span className='count-value'>{product.category_count}</span>
                </div>
            )
        },
        //  Ranking (default)
        {
            field: 'rank_week',
            sortField: SearchRankingSort.RANK_WEEK,
            label: i18n.t('searchRankings.columns.week', 'Week'),
            className: 'col-rank',
            centered: true,
            group: 'Ranking',
            body: product => (
                <RankCell
                    rank={product.rank_week}
                    previousRank={product.rank_last_week}
                    tooltip={i18n.t('searchRankings.columns.tooltipRankWeek', 'Gemiddelde ranking afgelopen 7 dagen')}
                />
            )
        },
        {
            field: 'rank_month',
            sortField: SearchRankingSort.RANK_MONTH,
            label: i18n.t('searchRankings.columns.month', 'Maand'),
            className: 'col-rank',
            centered: true,
            group: 'Ranking',
            tooltip: i18n.t('searchRankings.columns.tooltipMonth', 'Data beschikbaar na 30 dagen tracking'),
            body: product => (
                <RankCell
                    rank={product.rank_month}
                    previousRank={product.rank_last_month}
                    tooltip={i18n.t('searchRankings.columns.tooltipRankMonth', 'Gemiddelde ranking afgelopen 30 dagen')}
                />
            )
        },
        //  Ranking (extra)
        {
            field: 'rank_quarter',
            sortField: SearchRankingSort.RANK_QUARTER,
            label: i18n.t('searchRankings.columns.quarter', 'Kwartaal'),
            className: 'col-rank',
            centered: true,
            group: 'Ranking',
            tooltip: i18n.t('searchRankings.columns.tooltipQuarter', 'Data beschikbaar na 90 dagen tracking'),
            body: product => (
                <RankCell
                    rank={product.rank_quarter}
                    previousRank={product.rank_last_quarter}
                    tooltip={i18n.t(
                        'searchRankings.columns.tooltipRankQuarter',
                        'Gemiddelde ranking afgelopen 90 dagen'
                    )}
                />
            )
        },
        //  Advertising
        {
            field: 'cpc_win_bid',
            sortField: SearchRankingSort.WINNING_BID,
            label: 'CPC',
            className: 'col-metric cpc',
            centered: true,
            group: 'Advertising',
            tooltip: 'Winnend bod voor advertenties',
            body: product => <span>{formatCurrency(product.winning_bid, '\u2014')}</span>
        },
        //  Performance (extra)
        {
            field: 'review_score',
            sortField: SearchRankingSort.REVIEW_SCORE,
            label: i18n.t('searchRankings.columns.review', 'Review'),
            className: 'col-metric-sm',
            centered: true,
            group: 'Performance',
            tooltip: i18n.t(
                'searchRankings.columns.tooltipReview',
                'Review data wordt automatisch geladen zodra er reviews beschikbaar zijn'
            ),
            wrapTooltip: true,
            body: product => (
                <span>
                    {product.review_score !== null ? (
                        <>
                            <Icon icon='mdi:star' style={{fontSize: '0.75rem', color: '#f59e0b'}} />{' '}
                            {product.review_score.toFixed(1)}
                        </>
                    ) : (
                        '\u2014'
                    )}
                </span>
            )
        },
        {
            field: 'review_count',
            sortField: SearchRankingSort.REVIEW_COUNT,
            label: i18n.t('searchRankings.columns.reviews', 'Reviews'),
            className: 'col-metric-sm',
            centered: true,
            group: 'Performance',
            tooltip: i18n.t(
                'searchRankings.columns.tooltipReview',
                'Review data wordt automatisch geladen zodra er reviews beschikbaar zijn'
            ),
            wrapTooltip: true,
            body: product => (
                <span>{product.review_count !== null ? product.review_count.toLocaleString() : '\u2014'}</span>
            )
        },
        {
            field: 'nps_score',
            sortField: SearchRankingSort.NPS,
            label: i18n.t('searchRankings.columns.nps', 'NPS'),
            className: 'col-metric-sm',
            centered: true,
            group: 'Performance',
            tooltip: i18n.t(
                'searchRankings.columns.tooltipNps',
                'Klanttevredenheid op basis van reviews. Wordt automatisch geladen zodra er reviews beschikbaar zijn'
            ),
            wrapTooltip: true,
            body: product => <span>{product.nps_score !== null ? product.nps_score : '\u2014'}</span>
        }
        //  Competition
        // {
        //     field: 'buybox_percentage',
        //     sortField: SearchRankingSort.BUY_BOX,
        //     label: i18n.t('searchRankings.columns.buyboxPercent', 'Koopblok %'),
        //     className: 'col-metric-sm',
        //     centered: true,
        //     group: 'Competition',
        //     tooltip: i18n.t('searchRankings.columns.tooltipBuyboxPercent', 'Percentage tijd met het koopblok'),
        //     body: product => <span>{formatPct(product.buybox_percentage)}</span>
        // }
    ];
}

export function getAllColumns(): Array<SearchRankingsColumnDef> {
    return buildAllColumns();
}

export function getDefaultVisibleFields(): Array<string> {
    return buildAllColumns().map(col => col.field);
}

export function getVisibleColumns(visibleFields: Array<string>): Array<SearchRankingsColumnDef> {
    return buildAllColumns().filter(col => visibleFields.includes(col.field));
}
